// src/useFaceDetection.js
// Custom React hook for face detection using face-api.js
// Detects 0 faces (not visible) or 2+ faces (cheating warning)

import { useState, useEffect, useRef, useCallback } from "react";
import * as faceapi from "face-api.js";

const MODEL_URL = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model";

let modelsLoaded = false;
let modelsLoading = false;

async function loadModels() {
  if (modelsLoaded) return true;
  if (modelsLoading) {
    // Wait for existing load
    await new Promise(resolve => {
      const interval = setInterval(() => {
        if (modelsLoaded) { clearInterval(interval); resolve(); }
      }, 200);
    });
    return true;
  }
  modelsLoading = true;
  try {
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
    ]);
    modelsLoaded = true;
    console.log("face-api.js models loaded");
    return true;
  } catch (err) {
    console.error("Failed to load face detection models:", err);
    modelsLoading = false;
    return false;
  }
}

export function useFaceDetection(videoRef, isActive) {
  const [faceStatus, setFaceStatus] = useState("idle"); // idle | ok | no-face | multiple
  const [faceCount, setFaceCount] = useState(0);
  const intervalRef = useRef(null);
  const [modelReady, setModelReady] = useState(false);

  // Load models once
  useEffect(() => {
    loadModels().then(ok => setModelReady(ok));
  }, []);

  const runDetection = useCallback(async () => {
    if (!videoRef?.current || !modelReady) return;
    const video = videoRef.current;
    if (video.readyState < 2) return; // Video not ready

    try {
      const detections = await faceapi.detectAllFaces(
        video,
        new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 })
      );
      const count = detections.length;
      setFaceCount(count);
      if (count === 0) setFaceStatus("no-face");
      else if (count >= 2) setFaceStatus("multiple");
      else setFaceStatus("ok");
    } catch (err) {
      // Silently ignore detection errors
    }
  }, [videoRef, modelReady]);

  // Start/stop detection interval
  useEffect(() => {
    if (isActive && modelReady) {
      // Initial run after short delay
      const timeout = setTimeout(() => {
        runDetection();
        intervalRef.current = setInterval(runDetection, 3000);
      }, 2000);
      return () => {
        clearTimeout(timeout);
        clearInterval(intervalRef.current);
      };
    } else {
      clearInterval(intervalRef.current);
      setFaceStatus("idle");
    }
  }, [isActive, modelReady, runDetection]);

  const getFaceWarning = () => {
    if (faceStatus === "no-face") return "⚠️ Face not visible. Please look at the camera.";
    if (faceStatus === "multiple") return "🚨 Multiple persons detected. This has been flagged.";
    return null;
  };

  return { faceStatus, faceCount, faceWarning: getFaceWarning(), modelReady };
}
